import { Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import type { AddressInput, AddressVerificationResponse } from '@/api/address';
import { verifyAddress } from '@/api/address';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'VI', name: 'Virgin Islands' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

export interface AddressFormData {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
}

export type AddressVerificationStatus = 'verified' | 'not_verified' | 'not_checked';

interface AddressFormFieldsProps {
  address: AddressFormData;
  errors: Record<string, string>;
  onChange: (field: keyof AddressFormData, value: string) => void;
  onVerificationResult?: (result: AddressVerificationResponse) => void;
  onVerificationStatusChange?: (status: AddressVerificationStatus) => void;
  disabled?: boolean;
  /** When true, address fields (except line 2) show required asterisks. Defaults to true. */
  required?: boolean;
  /** Pass with accountId to persist verification to account_verifications */
  partyId?: string | null;
  /** Pass with partyId to persist verification to account_verifications */
  accountId?: string | null;
}

export function AddressFormFields({
  address,
  errors,
  onChange,
  onVerificationResult,
  onVerificationStatusChange,
  disabled = false,
  required = true,
  partyId,
  accountId,
}: AddressFormFieldsProps) {
  const [isVerifying, setIsVerifying] = useState(false);

  // Trigger address verification when user blurs from ZIP code field
  const handleZipCodeBlur = useCallback(async () => {
    // Only verify if we have enough data
    if (!address.address1 || !address.city || !address.state || !address.zipCode) {
      return;
    }

    // Validate ZIP code format
    if (!/^\d{5}(-\d{4})?$/.test(address.zipCode)) {
      return;
    }

    setIsVerifying(true);

    try {
      const addressInput: AddressInput = {
        streetAddress: address.address1,
        secondaryAddress: address.address2 || null,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        ...(partyId && { partyId }),
        ...(accountId && { accountId }),
      };

      const result = await verifyAddress(addressInput);

      if (onVerificationResult) {
        onVerificationResult(result);
      }

      if (onVerificationStatusChange) {
        if (result.verified) {
          onVerificationStatusChange('verified');
        } else if (
          result.error?.code === 'NOT_CONFIGURED' ||
          result.error?.code === 'NETWORK_ERROR'
        ) {
          onVerificationStatusChange('not_checked');
        } else {
          onVerificationStatusChange('not_verified');
        }
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: fine for web-app to log verification errors
      console.error('Address verification failed:', error);
      if (onVerificationStatusChange) {
        onVerificationStatusChange('not_checked');
      }
    } finally {
      setIsVerifying(false);
    }
  }, [address, partyId, accountId, onVerificationResult, onVerificationStatusChange]);

  return (
    <div className="space-y-4">
      {/* Street Address */}
      <div className="space-y-2">
        <Label htmlFor="address1">
          Street Address {required && <span className="text-destructive">*</span>}
        </Label>
        <Input
          id="address1"
          placeholder="123 Main Street"
          value={address.address1}
          onChange={(e) => onChange('address1', e.target.value)}
          className={errors.address1 ? 'border-destructive' : ''}
          disabled={disabled}
        />
        {errors.address1 && <p className="text-sm text-destructive">{errors.address1}</p>}
      </div>

      {/* Address Line 2 */}
      <div className="space-y-2">
        <Label htmlFor="address2">Apartment, Suite, Unit (Optional)</Label>
        <Input
          id="address2"
          placeholder="Apt 4B"
          value={address.address2}
          onChange={(e) => onChange('address2', e.target.value)}
          disabled={disabled}
        />
      </div>

      {/* City and State */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="city">
            City {required && <span className="text-destructive">*</span>}
          </Label>
          <Input
            id="city"
            placeholder="New York"
            value={address.city}
            onChange={(e) => onChange('city', e.target.value)}
            className={errors.city ? 'border-destructive' : ''}
            disabled={disabled}
          />
          {errors.city && <p className="text-sm text-destructive">{errors.city}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="state">
            State {required && <span className="text-destructive">*</span>}
          </Label>
          <Select
            value={address.state}
            onValueChange={(value) => onChange('state', value)}
            disabled={disabled}
          >
            <SelectTrigger className={errors.state ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.code} value={state.code}>
                  {state.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.state && <p className="text-sm text-destructive">{errors.state}</p>}
        </div>
      </div>

      {/* ZIP Code */}
      <div className="space-y-2">
        <Label htmlFor="zipCode">
          ZIP Code {required && <span className="text-destructive">*</span>}
          {isVerifying && (
            <span className="ml-2 inline-flex items-center text-muted-foreground">
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              Verifying...
            </span>
          )}
        </Label>
        <Input
          id="zipCode"
          placeholder="10001"
          value={address.zipCode}
          onChange={(e) => {
            // Allow only digits and hyphen
            const value = e.target.value.replace(/[^\d-]/g, '');
            onChange('zipCode', value);
          }}
          onBlur={handleZipCodeBlur}
          maxLength={10}
          className={errors.zipCode ? 'border-destructive' : ''}
          disabled={disabled || isVerifying}
        />
        {errors.zipCode && <p className="text-sm text-destructive">{errors.zipCode}</p>}
        <p className="text-xs text-muted-foreground">5 digits or 5+4 format (e.g., 10001-1234)</p>
      </div>
    </div>
  );
}

export { US_STATES };
